/**
 * The product's own building blocks, composed from the shadcn primitives in
 * `components/ui`. Pages read as intent — a Panel, a Stat, a StatusBadge —
 * rather than as class strings, and a change to the look lands in one place.
 *
 * The layout rhythm lives here too, so every screen in the dashboard shares
 * it: a page is a centred column with a 20px gap between panels, a panel is a
 * bordered surface with 20px of padding (16px on a phone), and a page opens
 * with an eyebrow, a display title and a one-line lede.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { SessionStatus } from '../api/types';

// ---------- layout ----------

/**
 * A page: one centred column. Wide, but not unbounded — past about 1500px a
 * table's columns drift so far apart that a row stops reading as a row.
 */
export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8 lg:py-8',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function PageHeader({
  eyebrow,
  title,
  lede,
  actions,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow ? (
          <div className="mb-1.5 text-label tracking-[0.08em] uppercase text-ink-faint">
            {eyebrow}
          </div>
        ) : null}
        <h1 className="text-display text-ink">{title}</h1>
        {lede ? <p className="mt-1.5 max-w-[70ch] text-support text-ink-dim">{lede}</p> : null}
      </div>
      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">{actions}</div>
      ) : null}
    </div>
  );
}

/**
 * A panel. `flush` drops the padding for a surface whose child runs edge to
 * edge (a table, a list); such a child brings its own inset.
 */
export function Panel({
  title,
  description,
  action,
  flush,
  className,
  bodyClassName,
  children,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  flush?: boolean;
  className?: string;
  bodyClassName?: string;
  children: ReactNode;
}) {
  const hasHead = Boolean(title || action || description);
  return (
    <section
      className={cn(
        'flex min-w-0 flex-col rounded-panel border border-line bg-card shadow-panel',
        className,
      )}
    >
      {hasHead ? (
        <div
          className={cn(
            'flex flex-wrap items-start justify-between gap-x-4 gap-y-2',
            flush ? 'px-4 pt-4 pb-3 sm:px-5' : 'px-4 pt-4 pb-3 sm:px-5 sm:pt-5',
          )}
        >
          <div className="min-w-0">
            {title ? <h2 className="text-title text-ink">{title}</h2> : null}
            {description ? (
              <p className="mt-1 text-meta text-ink-dim">{description}</p>
            ) : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      ) : null}
      <div
        className={cn(
          'min-w-0 flex-1',
          flush ? '' : hasHead ? 'px-4 pb-4 sm:px-5 sm:pb-5' : 'p-4 sm:p-5',
          bodyClassName,
        )}
      >
        {children}
      </div>
    </section>
  );
}

// ---------- data ----------

/**
 * One figure in the headline band. The band is a single panel with hairline
 * dividers rather than six floating cards: six separate boxes at this size
 * read as clutter, and the dividers say "these belong together".
 */
export function StatBand({ children }: { children: ReactNode }) {
  return (
    <section className="grid grid-cols-2 overflow-hidden rounded-panel border border-line bg-card shadow-panel sm:grid-cols-3 xl:grid-cols-6">
      {children}
    </section>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'up' | 'down';
}) {
  return (
    <div className="min-w-0 border-b border-line px-4 py-4 last:border-b-0 sm:px-5 [&:nth-child(2n)]:border-l sm:[&:nth-child(2n)]:border-l-0 sm:[&:nth-child(3n+2)]:border-l sm:[&:nth-child(3n+3)]:border-l sm:[&:nth-child(n+4)]:border-b-0 xl:border-b-0 xl:[&:not(:first-child)]:border-l">
      <div className="truncate text-meta font-medium text-ink-dim">{label}</div>
      <div className="mt-1 text-metric text-ink">{value}</div>
      {hint ? (
        <div
          className={cn(
            'mt-1 truncate text-meta',
            tone === 'up' ? 'text-verified' : tone === 'down' ? 'text-error' : 'text-ink-faint',
          )}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

export type Tone = 'neutral' | 'verified' | 'retry' | 'error' | 'field';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-muted text-ink-dim',
  verified: 'bg-verified-soft text-verified',
  retry: 'bg-retry-soft text-retry',
  error: 'bg-error-soft text-error',
  field: 'bg-field-soft text-field',
};

/** A status chip. Pick the tone from the data's status, never from looks. */
export function StatusBadge({
  tone = 'neutral',
  live,
  className,
  children,
}: {
  tone?: Tone;
  live?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-meta font-semibold whitespace-nowrap',
        TONE_CLASS[tone],
        className,
      )}
    >
      {live ? (
        <span className="size-1.5 rounded-full bg-current animate-pulse-dot" aria-hidden />
      ) : null}
      {children}
    </span>
  );
}

export function SessionStatusBadge({ status }: { status: SessionStatus | string }) {
  switch (status) {
    case 'active':
      return (
        <StatusBadge tone="field" live>
          Live
        </StatusBadge>
      );
    case 'completed':
      return <StatusBadge tone="verified">Completed</StatusBadge>;
    case 'abandoned':
      return <StatusBadge tone="retry">Abandoned</StatusBadge>;
    case 'failed':
      return <StatusBadge tone="error">Failed</StatusBadge>;
    default:
      return <StatusBadge>{status}</StatusBadge>;
  }
}

export function Dot({ tone = 'neutral', className }: { tone?: Tone; className?: string }) {
  const fill =
    tone === 'verified'
      ? 'bg-verified'
      : tone === 'retry'
        ? 'bg-retry'
        : tone === 'error'
          ? 'bg-error'
          : tone === 'field'
            ? 'bg-field'
            : 'bg-ink-faint';
  return <span className={cn('size-2 shrink-0 rounded-full', fill, className)} aria-hidden />;
}

export function Progress({
  value,
  tone = 'field',
  className,
}: {
  value: number;
  tone?: 'field' | 'verified';
  className?: string;
}) {
  const pct = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <div
      className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn('h-full rounded-full', tone === 'verified' ? 'bg-verified' : 'bg-field')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

/** Initials, for a person with no photo. */
export function Initials({ name, className }: { name: string; className?: string }) {
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || '?';
  return (
    <span
      className={cn(
        'grid size-8 shrink-0 place-items-center rounded-full bg-muted text-meta font-semibold text-ink-dim',
        className,
      )}
      aria-hidden
    >
      {initials}
    </span>
  );
}

// ---------- states ----------

export function EmptyState({
  title,
  body,
  action,
  className,
}: {
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-2 px-6 py-12 text-center text-ink-dim',
        className,
      )}
    >
      <div className="text-subtitle text-ink">{title}</div>
      {body ? <div className="max-w-[46ch] text-support">{body}</div> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <EmptyState
      title="Something went wrong"
      body={message}
      action={
        retry ? (
          <Button variant="outline" size="sm" onClick={retry}>
            Try again
          </Button>
        ) : null
      }
    />
  );
}

export function SkeletonRows({ rows = 4, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-3 p-4 sm:p-5', className)}>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-5" style={{ width: `${100 - (i % 3) * 12}%` }} />
      ))}
    </div>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-4 animate-spin', className)} aria-hidden />;
}

export function Toast({ message, onDone }: { message: string | undefined; onDone: () => void }) {
  useEffect(() => {
    if (!message) return;
    const t = window.setTimeout(onDone, 4000);
    return () => window.clearTimeout(t);
  }, [message, onDone]);
  if (!message) return null;
  return (
    <div
      role="status"
      className="fixed bottom-6 left-1/2 z-60 -translate-x-1/2 rounded-control bg-ink px-4 py-2.5 text-support font-medium text-background shadow-pop"
    >
      {message}
    </div>
  );
}

/**
 * A verification photo at a fixed size, whatever the photo's own shape.
 *
 * The panel around it must not resize when a portrait capture follows a
 * landscape one, so the box is fixed and the image is cropped into it; the
 * whole frame is one click away in the lightbox. Photos come from object
 * storage over the network, so the box holds a shimmer until the bytes land
 * and says so plainly if they never do.
 */
export function PhotoThumb({
  src,
  alt = '',
  className,
  onOpen,
}: {
  src: string;
  alt?: string;
  className?: string;
  onOpen?: (src: string) => void;
}) {
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  // A new src is a new load, even if this component instance is reused.
  useEffect(() => {
    setState('loading');
  }, [src]);

  if (state === 'failed') {
    return (
      <div
        className={cn(
          'grid shrink-0 place-items-center rounded-control border border-line bg-muted px-2 text-center text-meta text-ink-faint',
          className,
        )}
      >
        Photo unavailable
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen?.(src)}
      disabled={!onOpen || state !== 'ready'}
      aria-label={onOpen ? `${alt || 'Photo'}, open full size` : undefined}
      className={cn(
        'group relative shrink-0 overflow-hidden rounded-control border border-line bg-muted',
        onOpen && state === 'ready' ? 'cursor-zoom-in' : 'cursor-default',
        className,
      )}
    >
      {state === 'loading' ? (
        <span
          className="absolute inset-0 animate-pulse bg-muted"
          aria-label="Loading photo"
          role="status"
        />
      ) : null}
      <img
        src={src}
        alt={alt}
        decoding="async"
        onLoad={() => setState('ready')}
        onError={() => setState('failed')}
        className={cn(
          'size-full object-cover transition-opacity duration-200',
          state === 'ready' ? 'opacity-100' : 'opacity-0',
        )}
      />
      {onOpen && state === 'ready' ? (
        <span className="pointer-events-none absolute inset-0 bg-black/0 transition-colors group-hover:bg-black/15" />
      ) : null}
    </button>
  );
}

export function Lightbox({ src, onClose }: { src: string | undefined; onClose: () => void }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    if (!src) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [src, onClose]);

  if (!src) return null;
  return (
    <div
      role="dialog"
      aria-label="Photo"
      onClick={onClose}
      className="fixed inset-0 z-50 grid cursor-zoom-out place-items-center bg-scrim p-6"
    >
      {!loaded ? <Spinner className="absolute size-6 text-white" /> : null}
      <img
        src={src}
        alt=""
        onLoad={() => setLoaded(true)}
        className={cn(
          'max-h-[90vh] max-w-full rounded-panel transition-opacity',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}

/** A tiny hook for transient UI messages. */
export function useToast(): [string | undefined, (m: string) => void, () => void] {
  const [message, setMessage] = useState<string | undefined>();
  return [message, setMessage, () => setMessage(undefined)];
}

// ---------- formatting ----------

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function formatPercent(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return `${Math.round(rate * 100)}%`;
}

export function timeAgo(iso: string, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  if (seconds < 45) return 'just now';
  if (seconds < 3600) return `${Math.max(1, Math.floor(seconds / 60))} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  const days = Math.floor(seconds / 86400);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function shortId(id: string): string {
  return id.slice(0, 8);
}

/**
 * A segmented control: two to four mutually exclusive choices, shown all at
 * once because the options are the point (a window, an outcome filter).
 */
export function Segmented<T extends string>({
  value,
  options,
  onChange,
  className,
  ariaLabel,
}: {
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
  className?: string;
  ariaLabel?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn('inline-flex gap-0.5 rounded-control bg-muted p-0.5', className)}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={cn(
              'rounded-[7px] px-3 py-1.5 text-meta font-semibold transition-colors',
              active
                ? 'bg-card text-ink shadow-panel'
                : 'text-ink-dim hover:text-ink',
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
